import { useState, type SVGProps } from "react";
import { ConflictOverride } from "../../components/modal/ConflictOverride";
import { useAppData } from "../../hooks/useAppData";
import type { Booking } from "../../hooks/bookingTypes";
import { useConflictGate } from "../../hooks/useConflictGate";
import { addDays, fmtDate, fmtDateApi, isNightSlot, parseDateApi, slotSort } from "../../utils/date";
import css from "./dashboard.module.css";

const IcX = (p: SVGProps<SVGSVGElement>) => (
  <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" {...p}><path d="M4 4l8 8M12 4l-8 8" /></svg>
);

/* ── Перенос из дашборда (28.08, владелец): дата ‹ ›, квест, слот; занятость
   текущего дня подсвечена, остальное валидирует сервер (409 «Слот уже занят»,
   конфликт-гейт). Тот же PUT /move, что у сетки ДК.
   12.10 (владелец: «таймслот фазмы на 19:00 заблокирован — а мы таро перенесли на этот квест
   и реально его там провели»): отказ гейта (окно уборки, связанный квест) больше не тупик —
   как в сетке ДК, ConflictOverride «ВСЁ РАВНО ПЕРЕНЕСТИ» повторяет перенос с force
   (бэк пишет forced_* с attention). Точный дубль слота force не продавливает. ── */
export function MoveSheet({ b, gridDate, onClose, onDone, onMove, busyKeys }: {
  b: Booking; gridDate: Date; onClose: () => void; onDone: () => void;
  onMove: (id: number, questId: string, date: string, time: string, force?: boolean) => Promise<void>;
  busyKeys: Set<string>;
}) {
  const app = useAppData();
  /* 03.09: сервер трактует date как день СЕТКИ (ночной слот сам уходит на +1) — ночную бронь
     (в БД она лежит следующим днём) стартуем с её дня сетки, иначе перенос уезжал на сутки вперёд */
  const [mDate, setMDate] = useState<Date>(() => {
    if (!b._realDate) return gridDate;
    const real = parseDateApi(b._realDate.slice(0, 10));
    return isNightSlot(b.time) ? addDays(real, -1) : real;
  });
  const [qOpen, setQOpen] = useState(false);
  const [selQuest, setSelQuest] = useState(b.questId);
  const [selTime, setSelTime] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const gate = useConflictGate((e) => setErr(e instanceof Error ? e.message : "Не удалось перенести"));
  const quests = app.quests.filter((q) => !!q.active);
  const qLabel = (id: string) => { const q = quests.find((x) => x.id === id); return q ? `${q.short_name || q.name}${q.branch_id ? ` · ${String(q.branch_id).toUpperCase()}` : ""}` : id; };
  const slots = app.slots.filter((sl) => sl.quest_id === selQuest && sl.active).map((sl) => sl.time).sort(slotSort);
  const sameDay = fmtDateApi(mDate) === fmtDateApi(gridDate);
  const submit = async () => {
    if (!selTime || busy) return;
    setBusy(true); setErr("");
    const [q, d, t] = [selQuest, fmtDateApi(mDate), selTime];
    try {
      const held = await gate.run(async (force) => { await onMove(b.id, q, d, t, force); onDone(); }, "ВСЁ РАВНО ПЕРЕНЕСТИ");
      if (held) { setBusy(false); return; }   // открыт ConflictOverride — решает человек
    } catch (e) { setErr(e instanceof Error ? e.message : "Не удалось перенести"); }
    setBusy(false);
  };
  const force = async () => { setBusy(true); try { await gate.retry(); } finally { setBusy(false); } };
  return (
    <div className={css.sheetHost} role="dialog" aria-modal="true">
      <div className={css.sheetBackdrop} onClick={onClose} />
      <div className={css.sheetPanel}>
        <div className={css.sheetHandle} />
        <div className={css.sheetHead}>
          <span className={css.sheetTitle}>Перенести · {b.time} {qLabel(b.questId)}</span>
          <button type="button" className={css.sheetClose} aria-label="Закрыть" onClick={onClose}><IcX /></button>
        </div>
        <div className={css.sheetBody}>
          <div className={css.sbSub}>Дата</div>
          <div className={css.mvDayRow}>
            <button type="button" className={css.mvDayBtn} onClick={() => setMDate((d) => addDays(d, -1))}>‹</button>
            <span className={css.mvDayCur}>{fmtDate(mDate)}</span>
            <button type="button" className={css.mvDayBtn} onClick={() => setMDate((d) => addDays(d, 1))}>›</button>
          </div>
          <div className={css.sbSub}>Квест</div>
          <div className={css.mvQWrap}>
            <button type="button" className={css.mvQBtn} onClick={() => setQOpen((v) => !v)}>{qLabel(selQuest)} ▾</button>
            {qOpen ? (
              <div className={css.mvQList}>
                {quests.map((q) => (
                  <button key={q.id} type="button" className={css.mvQItem} onClick={() => { setSelQuest(q.id); setSelTime(null); setQOpen(false); }}>
                    {qLabel(q.id)}
                  </button>
                ))}
              </div>
            ) : null}
          </div>
          <div className={css.sbSub}>Время</div>
          <div className={css.presetRow}>
            {slots.length === 0 ? <span className={css.esS}>У квеста нет активных слотов</span> : null}
            {slots.map((t) => {
              const taken = sameDay && busyKeys.has(`${selQuest}_${t}`);
              return (
                <button key={t} type="button" disabled={taken}
                  className={`${css.preset} ${selTime === t ? css.on : ""} ${taken ? css.mvTaken : ""}`}
                  onClick={() => setSelTime(t)}>
                  {t}
                </button>
              );
            })}
          </div>
          {err ? <div className={css.rateErr}>{err}</div> : null}
        </div>
        <div className={css.sheetFoot}>
          <button type="button" className={css.sheetSubmit} disabled={!selTime || busy} onClick={() => void submit()}>
            {busy ? "Переношу…" : selTime ? `Перенести на ${fmtDate(mDate)} ${selTime}` : "Выбери время"}
          </button>
        </div>
      </div>
      {gate.conflicts ? (
        <ConflictOverride conflicts={gate.conflicts} actionLabel={gate.actionLabel} onForce={() => void force()} onDismiss={gate.clear} />
      ) : null}
    </div>
  );
}
